import { useCallback, useEffect, useMemo, useState } from "react";
import { Check, Clock, History, PenLine, Pencil, X, CalendarOff, ListChecks } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { ESTADO_BADGE, fmtFecha, fmtHora, fmtHoras, fmtDuracion, TIPO_SOLICITUD, etiquetaEstadoSolicitud } from "@/lib/format";
import { agruparSolicitudes, diaSolicitud } from "@/lib/solicitudesAgrupar";
import PageHeader from "@/components/PageHeader";
import RegistrarAusenciaDialog from "@/components/RegistrarAusenciaDialog";
import RevisionAusenciaJustificadaDialog from "@/components/RevisionAusenciaJustificadaDialog";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const TIPOS_FILTRO = [
  { id: "all", label: "Todas" },
  { id: "fichaje_manual", label: "Fichajes manuales" },
  { id: "vacaciones", label: "Vacaciones" },
  { id: "asuntos_propios", label: "Asuntos propios" },
  { id: "ausencia_justificada", label: "Ausencia justificada" },
  { id: "fuera_horario", label: "Fuera de horario" },
  { id: "hora_extra", label: "Horas extra" },
  { id: "baja", label: "Bajas" },
];

const TIPOS_CORREGIBLES = new Set(["vacaciones", "asuntos_propios", "ausencia", "baja"]);

const ES_FICHAJE = (tipo) =>
  tipo === "fichaje_manual" || tipo === "fuera_horario" || tipo === "modificacion_fichaje";

function DetalleSolicitud({ s }) {
  const tipoFichaje = s.tipo_fichaje === "salida" ? "salida" : s.tipo_fichaje === "entrada" ? "entrada" : s.tipo_fichaje;
  return (
    <div className="mt-2 space-y-1 text-sm text-zinc-600">
      {(s.tipo === "fichaje_manual" || s.tipo === "fuera_horario") && (
        <>
          {s.timestamp_solicitado && (
            <p>
              {s.tipo === "fichaje_manual" ? "Quiere añadir" : "Fichaje solicitado"}:{" "}
              <strong>
                {tipoFichaje} el {fmtFecha(s.timestamp_solicitado)} a las {fmtHora(s.timestamp_solicitado)}
              </strong>
            </p>
          )}
          {s.timestamp_real && (
            <p>
              Solicitud enviada: {fmtFecha(s.timestamp_real)} {fmtHora(s.timestamp_real)}
            </p>
          )}
          {s.timestamp_aprobado && s.estado === "pendiente_confirmacion" && (
            <p className="text-indigo-700">
              Propuesta del encargado: {fmtFecha(s.timestamp_aprobado)} {fmtHora(s.timestamp_aprobado)}
            </p>
          )}
          {s.estado === "aprobada" && s.fichaje_id && (
            <p className="text-green-700">Fichaje registrado (picado #{s.fichaje_id})</p>
          )}
          {s.tipo === "fuera_horario" && (
            <p>
              Justificada: <strong>{s.justificada ? "Sí (no cuenta como falta)" : "No"}</strong>
            </p>
          )}
        </>
      )}
      {s.tipo === "modificacion_fichaje" && (
        <>
          {s.timestamp_solicitado && s.timestamp_aprobado ? (
            <p>
              Cambio solicitado:{" "}
              <strong className="line-through text-zinc-400">
                {fmtFecha(s.timestamp_solicitado)} {fmtHora(s.timestamp_solicitado)}
              </strong>
              {" → "}
              <strong>
                {fmtFecha(s.timestamp_aprobado)} {fmtHora(s.timestamp_aprobado)}
              </strong>
              {tipoFichaje ? ` (${tipoFichaje})` : ""}
            </p>
          ) : s.timestamp_aprobado || s.timestamp_solicitado ? (
            <p>
              Hora propuesta:{" "}
              <strong>
                {fmtFecha(s.timestamp_aprobado || s.timestamp_solicitado)}{" "}
                {fmtHora(s.timestamp_aprobado || s.timestamp_solicitado)}
              </strong>
              {tipoFichaje ? ` (${tipoFichaje})` : ""}
            </p>
          ) : null}
          {s.fichaje_id && (
            <p className="text-xs text-zinc-400">Picado #{s.fichaje_id}</p>
          )}
        </>
      )}
      {s.tipo === "hora_extra" && (
        <>
          <p>
            {s.fecha_fin && s.fecha_fin !== s.fecha_inicio ? (
              <>
                Periodo: <strong>{fmtFecha(s.fecha_inicio)} — {fmtFecha(s.fecha_fin)}</strong>
                {s.detalle?.computo
                  ? ` · cómputo ${s.detalle.computo === "mes" ? "mensual" : "semanal"}`
                  : ""}
              </>
            ) : (
              <>
                Día: <strong>{fmtFecha(s.fecha_inicio)}</strong>
              </>
            )}
            {s.segundos != null && (
              <>
                {" "}· Exceso: <strong>{fmtHoras(s.segundos)}</strong>
              </>
            )}
          </p>
          {s.motivo && <p className="text-zinc-500">Motivo: {s.motivo}</p>}
        </>
      )}
      {s.tipo === "cambio_jornada" && (
        <>
          <p>
            {fmtFecha(s.fecha_inicio)}
            {s.fecha_fin !== s.fecha_inicio ? ` — ${fmtFecha(s.fecha_fin)}` : ""}
            {s.dias != null ? ` · ${s.dias} día(s)` : ""}
          </p>
          {(s.cambio_jornada || s.detalle)?.franjas && (
            <p>
              Nueva jornada:{" "}
              <strong>
                {(s.cambio_jornada || s.detalle).franjas_label ||
                  ((s.cambio_jornada || s.detalle).franjas || [])
                    .map((f) => `${f.inicio}–${f.fin}`)
                    .join(" · ")}
              </strong>
              {(s.cambio_jornada || s.detalle).minutos != null && (
                <>
                  {" "}
                  ({Math.floor((s.cambio_jornada || s.detalle).minutos / 60)} h
                  {(s.cambio_jornada || s.detalle).minutos % 60
                    ? ` ${(s.cambio_jornada || s.detalle).minutos % 60} min`
                    : ""}
                  )
                </>
              )}
            </p>
          )}
          {(s.cambio_jornada || s.detalle)?.dias_aplicables?.[0]?.franjas_habituales && (
            <p className="text-xs text-zinc-500">
              Habitual (1.er día):{" "}
              {(s.cambio_jornada || s.detalle).dias_aplicables[0].franjas_habituales
                .map((f) => `${f.inicio}–${f.fin}`)
                .join(" · ")}
            </p>
          )}
          {s.motivo && <p className="text-zinc-500">&quot;{s.motivo}&quot;</p>}
        </>
      )}
      {s.tipo === "ausencia_justificada" && (
        <>
          <p>
            {fmtFecha(s.fecha_inicio)}
            {s.fecha_fin !== s.fecha_inicio ? ` — ${fmtFecha(s.fecha_fin)}` : ""}
            {s.alcance_ausencia === "dia_completo" ? " · día completo" : s.alcance_ausencia === "tramo" ? " · tramo" : ""}
          </p>
          {(s.ausencia_unidades || []).length > 0 ? (
            <ul className="space-y-0.5">
              {(s.ausencia_unidades || []).map((u) => (
                <li key={u.id || `${u.franja_inicio}-${u.franja_fin}`}>
                  <strong>
                    {u.franja_inicio}–{u.franja_fin}
                  </strong>
                  {fmtDuracion(u.segundos) ? ` · ${fmtDuracion(u.segundos)}` : ""}
                  {u.fin_si_no_vuelve && u.fin_si_no_vuelve !== u.franja_fin ? (
                    <span className="text-xs text-zinc-400"> · si no vuelve, hasta {u.fin_si_no_vuelve}</span>
                  ) : null}
                </li>
              ))}
            </ul>
          ) : s.segundos ? (
            <p>
              Duración: <strong>{fmtDuracion(s.segundos) || fmtHoras(s.segundos)}</strong>
            </p>
          ) : null}
        </>
      )}
      {!(
        s.tipo === "fichaje_manual" ||
        s.tipo === "fuera_horario" ||
        s.tipo === "modificacion_fichaje" ||
        s.tipo === "hora_extra" ||
        s.tipo === "cambio_jornada" ||
        s.tipo === "ausencia_justificada"
      ) && (
        <p>
          {fmtFecha(s.fecha_inicio)} — {fmtFecha(s.fecha_fin)} · {s.dias} día(s) lab.
        </p>
      )}
      {s.motivo &&
        s.tipo !== "hora_extra" &&
        s.tipo !== "cambio_jornada" && <p className="text-zinc-500">&quot;{s.motivo}&quot;</p>}
    </div>
  );
}

function FilaFichajeResumen({ s }) {
  const tipo = s.tipo_fichaje || "";
  const esMod = s.tipo === "modificacion_fichaje";
  return (
    <li className="flex flex-wrap items-baseline justify-between gap-2 rounded-lg bg-zinc-50 px-3 py-2 text-sm">
      <div className="min-w-0">
        {esMod && s.timestamp_solicitado && s.timestamp_aprobado ? (
          <span className="font-medium text-zinc-900">
            <span className="text-zinc-400 line-through">{fmtHora(s.timestamp_solicitado)}</span>
            {" → "}
            {fmtHora(s.timestamp_aprobado)}
            <span className="ml-2 font-normal text-zinc-600">({tipo})</span>
          </span>
        ) : (
          <>
            <span className="font-medium text-zinc-900">
              {s.timestamp_solicitado ? fmtHora(s.timestamp_solicitado) : "—"}
            </span>
            <span className="ml-2 text-zinc-600">
              ({tipo}{s.tipo === "fichaje_manual" ? " · añadir" : ""})
            </span>
          </>
        )}
        {s.timestamp_real && (
          <span className="ml-2 text-xs text-zinc-400">
            enviada {fmtFecha(s.timestamp_real)} {fmtHora(s.timestamp_real)}
          </span>
        )}
        {s.motivo && <p className="mt-0.5 truncate text-xs text-zinc-500">&quot;{s.motivo}&quot;</p>}
      </div>
    </li>
  );
}

export default function Solicitudes() {
  const { user } = useAuth();
  const esAdmin = user?.rol === "admin";
  const [todas, setTodas] = useState([]);
  const [intercambiosPend, setIntercambiosPend] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [ausenciaOpen, setAusenciaOpen] = useState(false);
  const [filtroTipo, setFiltroTipo] = useState("all");
  const [revision, setRevision] = useState(null);
  const [modoRevision, setModoRevision] = useState("aprobar");
  const [comentario, setComentario] = useState("");
  const [horaMod, setHoraMod] = useState("");
  const [revisionAusencia, setRevisionAusencia] = useState(null);
  const [historialOpen, setHistorialOpen] = useState(null);
  const [historial, setHistorial] = useState([]);
  const [grupoRevision, setGrupoRevision] = useState(null);
  const [comentarioGrupo, setComentarioGrupo] = useState("");
  const [asuntosPropiosActivo, setAsuntosPropiosActivo] = useState(false);
  const [permisosAprobacion, setPermisosAprobacion] = useState(null);
  const [correccion, setCorreccion] = useState(null);
  const [corrAccion, setCorrAccion] = useState("modificar_periodo");
  const [corrForm, setCorrForm] = useState({ fecha_inicio: "", fecha_fin: "", comentario: "" });
  const [corrGuardando, setCorrGuardando] = useState(false);

  const cargar = useCallback(async () => {
    try {
      const [s, u, inter, func] = await Promise.all([
        api.get("/solicitudes"),
        api.get("/usuarios"),
        api.get("/intercambios/pendientes-revision").catch(() => ({ data: [] })),
        api.get("/solicitudes/funcionalidades").catch(() => ({ data: { asuntos_propios_activo: false } })),
      ]);
      setTodas(s.data);
      setUsuarios(u.data);
      setIntercambiosPend(inter.data || []);
      setAsuntosPropiosActivo(!!func.data?.asuntos_propios_activo);
      setPermisosAprobacion(func.data?.permisos_aprobacion ?? null);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, []);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const tiposFiltro = useMemo(
    () => TIPOS_FILTRO.filter((t) => t.id !== "asuntos_propios" || asuntosPropiosActivo),
    [asuntosPropiosActivo],
  );

  const puedeRegistrarAlgunTipo = useMemo(() => {
    if (!permisosAprobacion) return false;
    const tiposRegistro = ["vacaciones", "baja", "asuntos_propios", "ausencia_justificada"];
    return tiposRegistro.some((id) => {
      if (id === "asuntos_propios" && !asuntosPropiosActivo) return false;
      return permisosAprobacion[id] !== false;
    });
  }, [permisosAprobacion, asuntosPropiosActivo]);

  useEffect(() => {
    if (!asuntosPropiosActivo && filtroTipo === "asuntos_propios") {
      setFiltroTipo("all");
    }
  }, [asuntosPropiosActivo, filtroTipo]);

  const filtradas = useMemo(() => {
    if (filtroTipo === "all") return todas;
    if (filtroTipo === "asuntos_propios") return todas.filter((s) => s.tipo === "asuntos_propios" || s.tipo === "ausencia");
    return todas.filter((s) => s.tipo === filtroTipo);
  }, [todas, filtroTipo]);

  const pendientes = filtradas.filter((s) => s.estado === "pendiente");
  const denegadas = filtradas.filter((s) => s.estado === "denegada_trabajador");
  const historialLista = filtradas.filter((s) => !["pendiente", "denegada_trabajador"].includes(s.estado));

  const pendientesAgrupadas = useMemo(() => agruparSolicitudes(pendientes), [pendientes]);
  const denegadasAgrupadas = useMemo(() => agruparSolicitudes(denegadas), [denegadas]);

  const abrirRevision = (s, modo) => {
    if (s.tipo === "ausencia_justificada" && modo !== "modificar") {
      setRevisionAusencia(s);
      return;
    }
    setRevision(s);
    setModoRevision(modo);
    setComentario("");
    setHoraMod(s.timestamp_solicitado?.slice(0, 16) || "");
  };

  const [enviandoRevision, setEnviandoRevision] = useState(false);

  const enviarRevision = async () => {
    if (enviandoRevision || !revision) return;
    setEnviandoRevision(true);
    try {
      const body = { accion: modoRevision, comentario };
      if (modoRevision === "modificar") {
        if (!horaMod) {
          toast.error("Indica la hora aprobada");
          return;
        }
        body.timestamp_aprobado = horaMod;
      }
      await api.put(`/solicitudes/${revision.id}/revisar`, body);
      const esFichajeAprobado =
        modoRevision === "aprobar" && (revision.tipo === "fichaje_manual" || revision.tipo === "fuera_horario");
      toast.success(esFichajeAprobado ? "Fichaje aprobado y registrado" : "Solicitud actualizada");
      setRevision(null);
      cargar();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setEnviandoRevision(false);
    }
  };

  const revisarSolicitud = async (s, accion, opts = {}) => {
    const body = { accion, comentario: opts.comentario ?? "" };
    if (accion === "modificar") {
      if (!opts.timestamp_aprobado) throw new Error("Indica la hora aprobada");
      body.timestamp_aprobado = opts.timestamp_aprobado;
    }
    await api.put(`/solicitudes/${s.id}/revisar`, body);
  };

  const aprobarGrupo = async (items) => {
    try {
      for (const s of items) {
        await revisarSolicitud(s, "aprobar");
      }
      toast.success(`${items.length} solicitudes aprobadas`);
      cargar();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const abrirGrupoRevision = (items) => {
    setComentarioGrupo("");
    setGrupoRevision({
      items,
      decisions: Object.fromEntries(
        items.map((s) => [
          s.id,
          {
            accion: "",
            horaMod: s.timestamp_solicitado?.slice(0, 16) || "",
          },
        ])
      ),
    });
  };

  const actualizarDecisionGrupo = (id, campo, valor) => {
    setGrupoRevision((prev) => ({
      ...prev,
      decisions: {
        ...prev.decisions,
        [id]: { ...prev.decisions[id], [campo]: valor },
      },
    }));
  };

  const [enviandoGrupo, setEnviandoGrupo] = useState(false);

  const enviarGrupoRevision = async () => {
    if (!grupoRevision || enviandoGrupo) return;
    const sinDecision = grupoRevision.items.filter((s) => !grupoRevision.decisions[s.id]?.accion);
    if (sinDecision.length > 0) {
      toast.error(
        `Debes indicar qué hacer con las ${sinDecision.length} solicitud(es) restante(s): aprobar, modificar o rechazar`
      );
      return;
    }
    setEnviandoGrupo(true);
    try {
      for (const s of grupoRevision.items) {
        const d = grupoRevision.decisions[s.id];
        await revisarSolicitud(s, d.accion, {
          comentario: comentarioGrupo,
          timestamp_aprobado: d.accion === "modificar" ? d.horaMod : undefined,
        });
      }
      toast.success("Grupo revisado correctamente");
      setGrupoRevision(null);
      cargar();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setEnviandoGrupo(false);
    }
  };

  const verHistorial = async (s) => {
    try {
      const { data } = await api.get(`/solicitudes/${s.id}/historial`);
      setHistorial(data);
      setHistorialOpen(s);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const puedeCorregir = (s) =>
    esAdmin
    && TIPOS_CORREGIBLES.has(s.tipo)
    && (s.estado === "aprobada" || s.estado === "pendiente_confirmacion");

  const abrirCorreccion = (s) => {
    setCorreccion(s);
    setCorrAccion("modificar_periodo");
    setCorrForm({
      fecha_inicio: s.fecha_inicio || "",
      fecha_fin: s.fecha_fin || "",
      comentario: "",
    });
  };

  const guardarCorreccion = async () => {
    if (!correccion) return;
    if (!corrForm.comentario.trim() || corrForm.comentario.trim().length < 3) {
      toast.error("Indica el motivo de la corrección (autorización del trabajador, mín. 3 caracteres)");
      return;
    }
    setCorrGuardando(true);
    try {
      const body = {
        accion: corrAccion,
        comentario: corrForm.comentario.trim(),
      };
      if (corrAccion === "modificar_periodo") {
        body.fecha_inicio = corrForm.fecha_inicio;
        body.fecha_fin = corrForm.fecha_fin;
      }
      await api.put(`/solicitudes/${correccion.id}/corregir-admin`, body);
      toast.success(
        corrAccion === "cancelar"
          ? "Ausencia cancelada"
          : "Corrección guardada. El trabajador debe confirmarla en Mis solicitudes."
      );
      setCorreccion(null);
      cargar();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setCorrGuardando(false);
    }
  };

  const revisarIntercambio = async (item, accion) => {
    try {
      const path =
        item.ambito === "turno"
          ? `/intercambios/turno/${item.id}/revisar`
          : `/zonas/solicitudes-cambio/${item.id}/revisar`;
      await api.put(path, { accion, comentario: "" });
      toast.success(accion === "aprobar" ? "Intercambio aprobado" : "Solicitud rechazada");
      cargar();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const accionesFichaje = (s) => (
    <>
      <Button
        size="sm"
        onClick={() => abrirRevision(s, "aprobar")}
        data-testid={`aprobar-${s.id}`}
        className="gap-1 rounded-lg bg-green-600 hover:bg-green-700"
      >
        <Check className="h-4 w-4" /> Aprobar
      </Button>
      <Button
        size="sm"
        variant="outline"
        onClick={() => abrirRevision(s, "modificar")}
        className="gap-1 rounded-lg border-indigo-200 text-indigo-700"
      >
        <Clock className="h-4 w-4" /> Modificar hora
      </Button>
      <Button
        size="sm"
        variant="outline"
        onClick={() => abrirRevision(s, "rechazar")}
        data-testid={`rechazar-${s.id}`}
        className="gap-1 rounded-lg border-red-200 text-red-600"
      >
        <X className="h-4 w-4" /> Rechazar
      </Button>
      <Button size="sm" variant="ghost" onClick={() => verHistorial(s)} className="gap-1 text-zinc-500">
        <History className="h-4 w-4" />
      </Button>
    </>
  );

  const Tarjeta = ({ s, acciones }) => (
    <div className="rounded-2xl border border-zinc-200 bg-white p-5 shadow-sm transition-shadow hover:shadow-md">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-semibold text-zinc-900">{s.usuario}</span>
            {s.departamento && <span className="text-xs text-indigo-600">{s.departamento}</span>}
            <Badge variant="outline" className={ESTADO_BADGE[s.estado] || ""}>
              {etiquetaEstadoSolicitud(s, { vista: "revisor" })}
            </Badge>
            {s.ambito_revision_label && (
              <Badge
                variant="outline"
                className={
                  s.ambito_revision === "gerencia"
                    ? "border-teal-200 bg-teal-50 text-teal-800"
                    : "border-sky-200 bg-sky-50 text-sky-800"
                }
              >
                {s.ambito_revision_label}
              </Badge>
            )}
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-2">
            <Badge variant="outline" className="border-violet-200 bg-violet-50 text-violet-800">
              {s.tipo_label || TIPO_SOLICITUD[s.tipo]}
            </Badge>
          </div>
          <DetalleSolicitud s={s} />
          {s.comentario_revision && (
            <p className="mt-2 rounded-lg bg-indigo-50 px-3 py-2 text-xs text-indigo-800">
              Respuesta: {s.comentario_revision}
            </p>
          )}
        </div>
        <div className="flex flex-wrap gap-2">{acciones}</div>
      </div>
    </div>
  );

  const TarjetaGrupo = ({ grupo, modoDenegada = false }) => {
    const { items, usuario, departamento, tipo, tipo_label, dia } = grupo;
    const n = items.length;
    const diaFmt = dia ? fmtFecha(`${dia}T12:00:00`) : null;
    const puedeRevisar = items.some((s) => s.puede_revisar);

    return (
      <div
        className="rounded-2xl border border-violet-200 bg-white p-5 shadow-sm transition-shadow hover:shadow-md"
        data-testid={`grupo-solicitud-${grupo.usuario_id}-${tipo}-${dia || "sin-dia"}`}
      >
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-semibold text-zinc-900">{usuario}</span>
              {departamento && <span className="text-xs text-indigo-600">{departamento}</span>}
              {diaFmt && (
                <span className="text-xs font-medium text-zinc-500">{diaFmt}</span>
              )}
              <Badge variant="outline" className="border-amber-200 bg-amber-50 text-amber-800">
                {n} pendiente{n > 1 ? "s" : ""}
              </Badge>
              {items[0]?.ambito_revision_label && (
                <Badge
                  variant="outline"
                  className={
                    items[0].ambito_revision === "gerencia"
                      ? "border-teal-200 bg-teal-50 text-teal-800"
                      : "border-sky-200 bg-sky-50 text-sky-800"
                  }
                >
                  {items[0].ambito_revision_label}
                </Badge>
              )}
            </div>
            <div className="mt-1">
              <Badge variant="outline" className="border-violet-200 bg-violet-50 text-violet-800">
                {tipo_label || TIPO_SOLICITUD[tipo]}
              </Badge>
            </div>

            <ul className="mt-3 space-y-1">
              {items.map((s) => (
                <FilaFichajeResumen key={s.id} s={s} />
              ))}
            </ul>
          </div>

          <div className="flex flex-col gap-2 sm:items-end">
            {puedeRevisar ? (
              <>
                {!modoDenegada && (
                  <Button
                    size="sm"
                    onClick={() => aprobarGrupo(items)}
                    className="gap-1 rounded-lg bg-green-600 hover:bg-green-700"
                    data-testid={`aprobar-grupo-${grupo.key}`}
                  >
                    <Check className="h-4 w-4" /> Aprobar todas ({n})
                  </Button>
                )}
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => abrirGrupoRevision(items)}
                  className="gap-1 rounded-lg border-indigo-200 text-indigo-700"
                  data-testid={`revisar-grupo-${grupo.key}`}
                >
                  <ListChecks className="h-4 w-4" />
                  {modoDenegada ? "Revisar una a una" : "Revisar detalle"}
                </Button>
              </>
            ) : (
              <span className="text-xs text-zinc-400">No tienes permiso para revisarlas</span>
            )}
            <Button
              size="sm"
              variant="ghost"
              onClick={() => verHistorial(items[0])}
              className="gap-1 text-zinc-500"
            >
              <History className="h-4 w-4" /> Historial
            </Button>
          </div>
        </div>
      </div>
    );
  };

  const renderEntrada = (entrada, modoDenegada = false) => {
    if (entrada.kind === "group") {
      return <TarjetaGrupo key={entrada.key} grupo={entrada} modoDenegada={modoDenegada} />;
    }
    const s = entrada.item;
    if (!s.puede_revisar) {
      return (
        <Tarjeta
          key={s.id}
          s={s}
          acciones={
            <div className="flex items-center gap-2">
              <span className="text-xs text-zinc-400">No tienes permiso para revisarla</span>
              <Button size="sm" variant="ghost" onClick={() => verHistorial(s)} className="gap-1 text-zinc-500">
                <History className="h-4 w-4" />
              </Button>
            </div>
          }
        />
      );
    }
    return (
      <Tarjeta
        key={s.id}
        s={s}
        acciones={
          modoDenegada ? (
            <>
              <Button size="sm" onClick={() => abrirRevision(s, "aprobar")} className="rounded-lg bg-green-600">
                Aprobar original
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => abrirRevision(s, "modificar")}
                className="rounded-lg border-indigo-200 text-indigo-700"
              >
                Nueva propuesta
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => abrirRevision(s, "rechazar")}
                className="rounded-lg border-red-200 text-red-600"
              >
                Rechazar
              </Button>
            </>
          ) : ES_FICHAJE(s.tipo) ? (
            accionesFichaje(s)
          ) : (
            <>
              <Button
                size="sm"
                onClick={() => abrirRevision(s, "aprobar")}
                data-testid={`aprobar-${s.id}`}
                className="gap-1 rounded-lg bg-green-600 hover:bg-green-700"
              >
                <Check className="h-4 w-4" /> Aprobar
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => abrirRevision(s, "rechazar")}
                data-testid={`rechazar-${s.id}`}
                className="gap-1 rounded-lg border-red-200 text-red-600"
              >
                <X className="h-4 w-4" /> Rechazar
              </Button>
              <Button size="sm" variant="ghost" onClick={() => verHistorial(s)} className="gap-1 text-zinc-500">
                <History className="h-4 w-4" />
              </Button>
            </>
          )
        }
      />
    );
  };

  const decisionesPendientesGrupo =
    grupoRevision?.items.filter((s) => !grupoRevision.decisions[s.id]?.accion).length ?? 0;

  return (
    <div className="space-y-6" data-testid="solicitudes-admin-page">
      <PageHeader
        icon={PenLine}
        title="Solicitudes"
        subtitle="Fichajes manuales, vacaciones, intercambios de turno y asuntos propios"
        sectionKey="solicitudes"
        brandGradient={false}
      >
        {puedeRegistrarAlgunTipo && (
        <Button
          onClick={() => setAusenciaOpen(true)}
          className="gap-1.5 bg-white/20 text-white hover:bg-white/30"
          data-testid="registrar-ausencia-header-btn"
        >
          <CalendarOff className="h-4 w-4" />
          Registrar ausencia
        </Button>
        )}
      </PageHeader>

      {intercambiosPend.length > 0 && (
        <div className="rounded-2xl border border-violet-200 bg-violet-50/60 p-4">
          <p className="text-sm font-semibold text-violet-900">
            Intercambios pendientes de revisión ({intercambiosPend.length})
          </p>
          <ul className="mt-3 space-y-2">
            {intercambiosPend.map((i) => (
              <li
                key={`${i.ambito}-${i.id}`}
                className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-violet-100 bg-white px-4 py-3 text-sm"
              >
                <div>
                  <span className="font-medium">{i.usuario}</span>
                  <Badge variant="outline" className="ml-2 border-violet-200 text-violet-700">
                    {i.ambito === "turno" ? "Turno" : "Zona"}
                  </Badge>
                  <span className="ml-2 text-zinc-500">
                    {fmtFecha(i.fecha_inicio)}
                    {i.usuario_intercambio_nombre ? ` ↔ ${i.usuario_intercambio_nombre}` : ""}
                  </span>
                  <p className="text-xs text-zinc-500">{i.motivo}</p>
                </div>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    className="h-8 bg-emerald-600 hover:bg-emerald-700"
                    onClick={() => revisarIntercambio(i, "aprobar")}
                  >
                    Aprobar
                  </Button>
                  <Button size="sm" variant="outline" className="h-8" onClick={() => revisarIntercambio(i, "rechazar")}>
                    Rechazar
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        {tiposFiltro.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setFiltroTipo(t.id)}
            className={`rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${
              filtroTipo === t.id
                ? "bg-violet-600 text-white shadow-sm"
                : "bg-white text-zinc-600 ring-1 ring-zinc-200 hover:bg-violet-50"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <Tabs defaultValue="pendientes">
        <TabsList className="rounded-xl bg-rose-50/80 p-1">
          <TabsTrigger value="pendientes" data-testid="tab-pendientes" className="rounded-lg data-[state=active]:bg-white">
            Pendientes{" "}
            {pendientes.length > 0 && (
              <span className="ml-1.5 rounded-full bg-amber-500 px-1.5 text-[10px] text-white">{pendientes.length}</span>
            )}
          </TabsTrigger>
          <TabsTrigger value="denegadas" className="rounded-lg data-[state=active]:bg-white">
            Rechazadas por trabajador{" "}
            {denegadas.length > 0 && (
              <span className="ml-1.5 rounded-full bg-orange-500 px-1.5 text-[10px] text-white">{denegadas.length}</span>
            )}
          </TabsTrigger>
          <TabsTrigger value="historial" data-testid="tab-historial" className="rounded-lg data-[state=active]:bg-white">
            Historial
          </TabsTrigger>
        </TabsList>

        <TabsContent value="pendientes" className="mt-4 space-y-3">
          {pendientes.length === 0 && (
            <p className="rounded-2xl border border-dashed border-rose-200 bg-rose-50/30 p-8 text-center text-sm text-zinc-500">
              No hay solicitudes pendientes.
            </p>
          )}
          {pendientesAgrupadas.map((entrada) => renderEntrada(entrada, false))}
        </TabsContent>

        <TabsContent value="denegadas" className="mt-4 space-y-3">
          {denegadas.length === 0 && (
            <p className="rounded-2xl border border-dashed border-orange-200 bg-orange-50/30 p-8 text-center text-sm text-zinc-500">
              No hay solicitudes rechazadas por el trabajador.
            </p>
          )}
          {denegadasAgrupadas.map((entrada) => renderEntrada(entrada, true))}
        </TabsContent>

        <TabsContent value="historial" className="mt-4 space-y-3">
          {historialLista.map((s) => (
            <Tarjeta
              key={s.id}
              s={s}
              acciones={
                <div className="flex flex-wrap gap-1">
                  {puedeCorregir(s) && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => abrirCorreccion(s)}
                      className="gap-1 border-amber-200 text-amber-800 hover:bg-amber-50"
                      data-testid={`corregir-solicitud-${s.id}`}
                    >
                      <Pencil className="h-3.5 w-3.5" /> Corregir
                    </Button>
                  )}
                  <Button size="sm" variant="ghost" onClick={() => verHistorial(s)} className="gap-1 text-zinc-500">
                    <History className="h-4 w-4" /> Trazabilidad
                  </Button>
                </div>
              }
            />
          ))}
        </TabsContent>
      </Tabs>

      <Dialog open={!!revision} onOpenChange={(v) => !v && setRevision(null)}>
        <DialogContent data-testid="revision-dialog">
          <DialogHeader>
            <DialogTitle>
              {modoRevision === "aprobar" && "Aprobar solicitud"}
              {modoRevision === "rechazar" && "Rechazar solicitud"}
              {modoRevision === "modificar" && "Modificar hora del fichaje"}
            </DialogTitle>
          </DialogHeader>
          {revision && (
            <p className="text-sm text-zinc-500">
              {revision.usuario} · {revision.tipo_label || TIPO_SOLICITUD[revision.tipo]}
            </p>
          )}
          {modoRevision === "modificar" && (
            <div className="space-y-1.5">
              <label className="text-sm font-medium">Hora aprobada</label>
              <Input type="datetime-local" value={horaMod} onChange={(e) => setHoraMod(e.target.value)} />
            </div>
          )}
          <Textarea placeholder="Comentario para el trabajador" value={comentario} onChange={(e) => setComentario(e.target.value)} />
          <DialogFooter>
            <Button
              onClick={enviarRevision}
              disabled={enviandoRevision}
              className="bg-indigo-600 hover:bg-indigo-700"
            >
              {enviandoRevision ? "Enviando…" : "Confirmar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!grupoRevision} onOpenChange={(v) => !v && setGrupoRevision(null)}>
        <DialogContent className="max-w-2xl" data-testid="revision-grupo-dialog">
          <DialogHeader>
            <DialogTitle>Revisar solicitudes agrupadas</DialogTitle>
          </DialogHeader>
          {grupoRevision && (
            <>
              <p className="text-sm text-zinc-500">
                {grupoRevision.items[0]?.usuario} · {grupoRevision.items[0]?.tipo_label}
                {grupoRevision.items[0] && (
                  <> · {fmtFecha(`${diaSolicitud(grupoRevision.items[0])}T12:00:00`)}</>
                )}{" "}
                · {grupoRevision.items.length} solicitud(es)
              </p>
              <p className="text-xs text-amber-700">
                Indica una decisión para cada solicitud. No se puede confirmar hasta que todas tengan respuesta.
              </p>
              <div className="max-h-[50vh] space-y-3 overflow-y-auto pr-1">
                {grupoRevision.items.map((s) => {
                  const d = grupoRevision.decisions[s.id];
                  return (
                    <div
                      key={s.id}
                      className="mb-2 rounded-xl border border-zinc-200 bg-zinc-50/80 p-3"
                      data-testid={`grupo-item-${s.id}`}
                    >
                      <div className="flex flex-wrap items-start justify-between gap-2">
                        <div className="text-sm">
                          <span className="font-medium">
                            {fmtHora(s.timestamp_solicitado)} ({s.tipo_fichaje})
                          </span>
                          {s.motivo && <p className="mt-0.5 text-xs text-zinc-500">&quot;{s.motivo}&quot;</p>}
                        </div>
                        <Select
                          value={d.accion || ""}
                          onValueChange={(v) => actualizarDecisionGrupo(s.id, "accion", v)}
                        >
                          <SelectTrigger className="h-9 w-[180px] bg-white">
                            <SelectValue placeholder="Decisión…" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="aprobar">Aprobar</SelectItem>
                            <SelectItem value="modificar">Modificar hora</SelectItem>
                            <SelectItem value="rechazar">Rechazar</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      {d.accion === "modificar" && (
                        <div className="mt-2">
                          <label className="text-xs font-medium text-zinc-600">Hora aprobada</label>
                          <Input
                            type="datetime-local"
                            className="mt-1 bg-white"
                            value={d.horaMod}
                            onChange={(e) => actualizarDecisionGrupo(s.id, "horaMod", e.target.value)}
                          />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              <Textarea
                placeholder="Comentario común para el trabajador (opcional)"
                value={comentarioGrupo}
                onChange={(e) => setComentarioGrupo(e.target.value)}
              />
              {decisionesPendientesGrupo > 0 && (
                <p className="text-xs text-amber-600">
                  Faltan {decisionesPendientesGrupo} decisión(es) por indicar.
                </p>
              )}
            </>
          )}
          <DialogFooter>
            <Button
              onClick={enviarGrupoRevision}
              disabled={decisionesPendientesGrupo > 0 || enviandoGrupo}
              className="bg-indigo-600 hover:bg-indigo-700"
              data-testid="confirmar-grupo-revision"
            >
              {enviandoGrupo ? "Enviando…" : "Confirmar revisión del grupo"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!historialOpen} onOpenChange={(v) => !v && setHistorialOpen(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Historial de la solicitud</DialogTitle>
          </DialogHeader>
          <div className="max-h-80 space-y-3 overflow-y-auto">
            {historial.map((h) => {
              const d = h.detalle || {};
              const antI = d.fecha_inicio_anterior;
              const antF = d.fecha_fin_anterior;
              const nuevaI = d.fecha_inicio_nueva || d.fecha_inicio;
              const nuevaF = d.fecha_fin_nueva || d.fecha_fin;
              const hayCambioFechas = antI && antF && nuevaI && nuevaF;
              return (
                <div key={h.id} className="rounded-lg border border-zinc-100 bg-zinc-50 px-3 py-2 text-sm">
                  <p className="font-medium text-zinc-800">{h.accion.replace(/_/g, " ")}</p>
                  <p className="text-xs text-zinc-500">
                    {h.usuario} · {fmtFecha(h.created_at)} {fmtHora(h.created_at)}
                  </p>
                  {h.comentario && <p className="mt-1 text-zinc-600">{h.comentario}</p>}
                  {hayCambioFechas && (
                    <div className="mt-2 rounded-md border border-amber-100 bg-amber-50/80 px-2.5 py-2 text-xs text-amber-950">
                      <p>
                        <span className="font-medium">Periodo anterior:</span>{" "}
                        {fmtFecha(antI)} — {fmtFecha(antF)}
                      </p>
                      <p className="mt-0.5">
                        <span className="font-medium">Periodo nuevo:</span>{" "}
                        {fmtFecha(nuevaI)} — {fmtFecha(nuevaF)}
                      </p>
                    </div>
                  )}
                  {!hayCambioFechas && nuevaI && nuevaF && (h.accion || "").includes("modific") && (
                    <p className="mt-1 text-xs text-zinc-500">
                      Periodo: {fmtFecha(nuevaI)} — {fmtFecha(nuevaF)}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!correccion} onOpenChange={(v) => !v && setCorreccion(null)}>
        <DialogContent className="max-w-md" data-testid="correccion-admin-dialog">
          <DialogHeader>
            <DialogTitle>Corregir ausencia</DialogTitle>
          </DialogHeader>
          {correccion && (
            <>
              <p className="text-sm text-zinc-500">
                {correccion.usuario} · {correccion.tipo_label || TIPO_SOLICITUD[correccion.tipo]}
              </p>
              <p className="rounded-lg border border-amber-100 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                Disponible solo para el administrador. Tras modificar el periodo, el trabajador deberá confirmar
                la corrección en Mis solicitudes. Indica en el comentario que está autorizado por el trabajador.
              </p>
              <div className="space-y-3">
                <div className="space-y-1.5">
                  <Label>Tipo de corrección</Label>
                  <select
                    className="h-9 w-full rounded-md border border-zinc-200 px-2 text-sm"
                    value={corrAccion}
                    onChange={(e) => setCorrAccion(e.target.value)}
                  >
                    <option value="modificar_periodo">Cambiar periodo</option>
                    <option value="cancelar">Cancelar</option>
                  </select>
                </div>
                {corrAccion === "modificar_periodo" && (
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label>Desde</Label>
                      <Input
                        type="date"
                        value={corrForm.fecha_inicio}
                        onChange={(e) => setCorrForm({ ...corrForm, fecha_inicio: e.target.value })}
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label>Hasta</Label>
                      <Input
                        type="date"
                        value={corrForm.fecha_fin}
                        onChange={(e) => setCorrForm({ ...corrForm, fecha_fin: e.target.value })}
                      />
                    </div>
                  </div>
                )}
                <div className="space-y-1.5">
                  <Label>Motivo / autorización del trabajador</Label>
                  <Textarea
                    value={corrForm.comentario}
                    onChange={(e) => setCorrForm({ ...corrForm, comentario: e.target.value })}
                    placeholder="Ej. Corrección acordada con el trabajador el 30/07 por error en los días de mayo"
                  />
                </div>
              </div>
            </>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setCorreccion(null)}>Cancelar</Button>
            <Button onClick={guardarCorreccion} disabled={corrGuardando} className="bg-amber-700 hover:bg-amber-800">
              {corrGuardando ? "Guardando…" : "Guardar corrección"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <RegistrarAusenciaDialog
        open={ausenciaOpen}
        onOpenChange={setAusenciaOpen}
        usuarios={usuarios}
        asuntosPropiosActivo={asuntosPropiosActivo}
        permisosAprobacion={permisosAprobacion}
        onSuccess={cargar}
      />

      <RevisionAusenciaJustificadaDialog
        solicitud={revisionAusencia}
        open={!!revisionAusencia}
        onOpenChange={(v) => !v && setRevisionAusencia(null)}
        onSuccess={cargar}
      />
    </div>
  );
}
